import { InputAdornment } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"

import { useGetSalesCustomerSuggestions } from "../../hooks/useGetSalesCustomerSuggestions"
import type { SalesCustomer } from "../../types"
import { SUGGESTION_ID_PREFIX, parseSuggestionId } from "../../utils"

import { AerosAutocomplete } from "@/components/UiKit"

interface CustomerSearchSuggestionProps {
  searchInputValue: string
  onSearchInputChange: (value: string) => void
  onSearch: (value: string) => void
  onCustomerSelect: (customer: SalesCustomer) => void
}

export const CustomerSearchSuggestion: FC<CustomerSearchSuggestionProps> = ({
  searchInputValue,
  onSearchInputChange,
  onSearch,
  onCustomerSelect,
}) => {
  const { suggestions: suggestionCustomers } = useGetSalesCustomerSuggestions(searchInputValue)

  const suggestions = useMemo(
    () =>
      suggestionCustomers.map((customer) => ({
        value: `${SUGGESTION_ID_PREFIX}${customer.companyLocationAssociationId}`,
        label: customer.company.name,
      })),
    [suggestionCustomers]
  )

  const handleAutocompleteChange = (value: string | null) => {
    const associationId = parseSuggestionId(value)
    if (!associationId) return
    const matched = suggestionCustomers.find((customer) => customer.companyLocationAssociationId === associationId)
    if (!matched) return
    onCustomerSelect(matched)
  }

  const handleSearchInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch(searchInputValue)
    }
  }

  return (
    <AerosAutocomplete
      freeSolo
      options={suggestions}
      inputValue={searchInputValue}
      onInputChange={onSearchInputChange}
      onChange={handleAutocompleteChange}
      forcePopupIcon={false}
      placeholder='Search Customers or Address'
      slotProps={{
        textField: {
          onKeyDown: handleSearchInputKeyDown,
          slotProps: {
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          },
        },
      }}
      data-testid='sales.customer-search-suggestion.search-field'
    />
  )
}
